/**
 * The members of a STATIC list: paginated, searchable, with a type badge and a
 * remove-selected action. `reloadKey` is bumped by the page after anything that
 * changes membership (add contacts, an import) so this table re-reads.
 *
 * Selection is keyed `type:id` — a contact and a subscriber can share an id.
 */
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { api, ApiError } from '@/lib/api';
import { pluralise } from '@/lib/format';
import type { AudienceMember } from '@/lib/types';
import { useDebounced } from './ref-options';

/*
 * Re-exported because `SuppressionsTable` (settings agent) imports it from here.
 * Its home is `./ref-options`; import it from there in new code.
 */
export { useDebounced };

const PER_PAGE = 25;

/** Previous / next pager, shared by the three marketing list tables. */
export function Pager({
  page,
  totalPages,
  total,
  shown,
  noun,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  shown: number;
  noun: { singular: string; plural: string };
  onPage(page: number): void;
}) {
  return (
    <PanelFooter>
      <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
        {total === 0
          ? `No ${noun.plural} to show`
          : `Showing ${shown} of ${total} ${total === 1 ? noun.singular : noun.plural}`}
      </p>
      <div className="flex items-center gap-2">
        <span className="hidden text-xs text-fg-subtle tabular sm:inline">
          Page {page} of {Math.max(totalPages, 1)}
        </span>
        <Button
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-none"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-none"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </PanelFooter>
  );
}

const keyOf = (m: { type: string; id: number }) => `${m.type}:${m.id}`;

interface Result {
  rows: AudienceMember[];
  total: number;
  totalPages: number;
}

export function ListMembersTable({
  listId,
  canRemove,
  reloadKey,
  onChanged,
  actions,
  onAddContacts,
  onImport,
}: {
  /** Shown as a button in the empty state when the list itself is empty (not on an empty search). */
  onAddContacts?(): void;
  /** Shown as a button in the empty state when the list itself is empty (not on an empty search). */
  onImport?(): void;
  listId: number;
  canRemove: boolean;
  reloadKey: number;
  /** Called after a removal so the page can refresh counts. */
  onChanged?(): void;
  actions?: React.ReactNode;
}) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim());
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [localReload, setLocalReload] = useState(0);

  // A new search starts on page 1 — page 3 of a shorter result is usually empty.
  const [lastSearch, setLastSearch] = useState(debouncedSearch);
  if (lastSearch !== debouncedSearch) {
    setLastSearch(debouncedSearch);
    setPage(1);
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listMarketingListMembers(
        listId,
        { page, perPage: PER_PAGE, ...(debouncedSearch ? { search: debouncedSearch } : {}) },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({
          rows: response.data,
          total: response.meta.total,
          totalPages: response.meta.totalPages,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setResult(null);
        setError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'The members could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [listId, page, debouncedSearch, reloadKey, localReload]);

  const rows = result?.rows ?? [];
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(keyOf(r)));

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPage) rows.forEach((r) => next.delete(keyOf(r)));
      else rows.forEach((r) => next.add(keyOf(r)));
      return next;
    });
  };

  const remove = async () => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const members = [...selected].map((key) => {
        const [type, id] = key.split(':');
        return { type: type as 'contact' | 'subscriber', id: Number(id) };
      });
      await api.removeMarketingListMembers(listId, members);
      setSelected(new Set());
      setConfirmOpen(false);
      setLocalReload((n) => n + 1);
      onChanged?.();
    } catch (err) {
      setRemoveError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'The members could not be removed.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Panel>
      <PanelHeader
        title="Members"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canRemove && selected.size > 0 && (
              <Button variant="danger" size="sm" onClick={() => setConfirmOpen(true)}>
                Remove {selected.size} selected
              </Button>
            )}
            {actions}
          </div>
        }
      />
      <div className="border-t border-border px-4 py-3">
        <Input
          type="search"
          aria-label="Search members"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-sm"
        />
      </div>

      {error ? (
        <div className="px-4 pb-4">
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        </div>
      ) : loading && !result ? (
        <div className="grid gap-2 px-4 pb-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={debouncedSearch ? 'No members match' : 'No members yet'}
          description={debouncedSearch ? 'Try a different search.' : 'Add contacts or import a CSV to fill this list.'}
          action={
            // Only on the list's own emptiness, never on a search that returned nothing.
            !debouncedSearch && (onAddContacts || onImport) ? (
              <div className="flex flex-wrap justify-center gap-2">
                {onAddContacts && (
                  <Button variant="primary" size="sm" onClick={onAddContacts}>
                    Add contacts
                  </Button>
                )}
                {onImport && (
                  <Button variant="secondary" size="sm" onClick={onImport}>
                    Import CSV
                  </Button>
                )}
              </div>
            ) : undefined
          }
        />
      ) : (
        <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
          <Table className="min-w-[640px]">
            <THead>
              <TR>
                {canRemove && (
                  <TH className="w-10">
                    <Checkbox
                      label={<span className="sr-only">Select every member on this page</span>}
                      checked={allOnPage}
                      onChange={togglePage}
                    />
                  </TH>
                )}
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Type</TH>
                <TH>Company</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((member) => {
                const key = keyOf(member);
                return (
                  <TR key={key}>
                    {canRemove && (
                      <TD>
                        <Checkbox
                          label={<span className="sr-only">Select {member.email}</span>}
                          checked={selected.has(key)}
                          onChange={() => toggle(key)}
                        />
                      </TD>
                    )}
                    <TD className="max-w-[16rem] truncate" title={member.name}>
                      {member.name || '—'}
                    </TD>
                    <TD className="max-w-[18rem] truncate" title={member.email}>
                      <EmailLink address={member.email} />
                    </TD>
                    <TD>
                      <Badge tone="neutral" variant="outline">
                        {member.type === 'contact' ? 'Contact' : 'Subscriber'}
                      </Badge>
                    </TD>
                    <TD className="max-w-[16rem] truncate" title={member.companyName ?? undefined}>
                      {member.companyName ?? '—'}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </TableWrap>
      )}

      {result && (
        <Pager
          page={page}
          totalPages={result.totalPages}
          total={result.total}
          shown={rows.length}
          noun={{ singular: 'member', plural: 'members' }}
          onPage={setPage}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => (removing ? undefined : setConfirmOpen(false))}
        onConfirm={remove}
        title={`Remove ${pluralise(selected.size, 'member')} from this list?`}
        description="They come off this list only. Their contact or subscriber records stay as they are."
        confirmLabel="Remove from list"
        loading={removing}
        error={removeError}
      />
    </Panel>
  );
}
